import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  makeStyles,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { Ub04Form, getUB04FormsByPolicyNumber } from '../../services/ub04Service';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  searchInput: {
    flexGrow: 1,
    marginRight: theme.spacing(1),
  },
  searchButton: {
    minWidth: 'auto',
  },
  resultsList: {
    maxHeight: '400px',
    overflow: 'auto',
  },
  listItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  noResults: {
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
}));

interface SearchClaimsProps {
  onSelectClaim: (claim: Ub04Form) => void;
}

const SearchClaims: React.FC<SearchClaimsProps> = ({ onSelectClaim }) => {
  const classes = useStyles();
  const [policyNumber, setPolicyNumber] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Ub04Form[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const handleSearch = async () => {
    if (!policyNumber.trim()) return;

    setIsSearching(true);
    setHasSearched(true);

    try {
      const results = await getUB04FormsByPolicyNumber(policyNumber);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching for claims:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelectClaim = (claim: Ub04Form) => {
    onSelectClaim(claim);
  };

  return (
    <Paper className={classes.root}>
      <Typography variant="h6" gutterBottom>
        Search Claims
      </Typography>
      <Box className={classes.searchBox}>
        <TextField
          className={classes.searchInput}
          label="Policy Number"
          variant="outlined"
          size="small"
          value={policyNumber}
          onChange={(e) => setPolicyNumber(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <Button
          className={classes.searchButton}
          variant="contained"
          color="primary"
          onClick={handleSearch}
          disabled={isSearching || !policyNumber.trim()}
          startIcon={isSearching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
        >
          {isSearching ? 'Searching...' : 'Search'}
        </Button>
      </Box>

      {isSearching ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : hasSearched ? (
        searchResults.length > 0 ? (
          <List className={classes.resultsList}>
            {searchResults.map((claim, index) => (
              <React.Fragment key={claim.id || index}>
                <ListItem
                  className={classes.listItem}
                  onClick={() => handleSelectClaim(claim)}
                  button
                >
                  <ListItemText
                    primary={`Patient: ${claim.patientName}`}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="textPrimary">
                          Policy: {claim.policyNumber}
                        </Typography>
                        <br />
                        {claim.serviceStartDate && (
                          <>
                            Service Date: {claim.serviceStartDate instanceof Date 
                              ? claim.serviceStartDate.toLocaleDateString() 
                              : new Date(claim.serviceStartDate).toLocaleDateString()}
                            <br />
                          </>
                        )}
                        Provider: {claim.billingProviderName}
                      </>
                    }
                  />
                </ListItem>
                {index < searchResults.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box className={classes.noResults}>
            <Typography variant="body1">No claims found for this policy number.</Typography>
          </Box>
        )
      ) : null}
    </Paper>
  );
};

export default SearchClaims;
